'use client';

import { Icon, useToast } from '@/src/components/ui';
import Image from 'next/image';
import { Button } from '@workspace/ui/components/button';
import { cn } from '@workspace/ui/lib/utils';
import { ISocialPostComment } from 'types/src/types';
import { DotsButton } from '../DotsButton';
import { useBoolean } from '@/src/hooks/use-boolean';
import { Modal } from '../Modal';
import { ReplyForm } from './ReplyForm';
import { useState } from 'react';
import { useToggleCommentReactionMutation } from '@/src/hooks/use-toggle-comment-reaction-mutation';
import { QueryKey, useQueryClient } from '@tanstack/react-query';
import { superlikeComment } from '@/src/actions/social-comments';

type Props = {
  isMyComment: boolean;
  comment: ISocialPostComment;
  isAnonymous?: boolean;
  isMobile?: boolean;
  isShowReplies?: () => void;
  queryKey: QueryKey;
  postAuthorAvatarUrl?: string;
  isSuperliked?: boolean;
  isMyPost?: boolean;
  isAbleToSuperLike?: boolean;
};

export const CommentActions = ({
  isMyComment,
  comment,
  isAnonymous,
  isMobile,
  isShowReplies,
  queryKey,
  postAuthorAvatarUrl,
  isSuperliked,
  isMyPost,
  isAbleToSuperLike,
}: Props) => {
  const isReplyOpened = useBoolean(false);
  const [replyToId, setReplyToId] = useState<number | null>(null);
  const { reactionCounts, myReaction, repliesCount, parentId } = comment;
  const toggle = useToggleCommentReactionMutation();
  const toast = useToast();
  const qc = useQueryClient();

  const handleSuperlike = async () => {
    try {
      await superlikeComment(comment.id);

      qc.invalidateQueries({ queryKey: ['profile'] });

      qc.invalidateQueries({ queryKey });
      toast.success('Comment superliked!');
    } catch (error) {
      toast.error('Failed to superlike comment.');
      console.error('Error superliking comment:', error);
    }
  };

  const handleLike = () => {
    toggle.mutate({ commentId: comment.id, reaction: 'like', queryKey });
  };

  const handleDislike = () => {
    toggle.mutate({ commentId: comment.id, reaction: 'dislike', queryKey });
  };

  const score = reactionCounts?.score ?? 0;
  const liked = myReaction === 'like';
  const disliked = myReaction === 'dislike';

  return (
    <div className="flex justify-between md:mt-3 mt-2 w-full items-center">
      <div className="flex gap-2 items-end justify-center">
        <Button
          variant="ghost"
          size="sm"
          disabled={isAnonymous}
          onClick={() => {
            setReplyToId(comment.id);
            isReplyOpened.onTrue();
          }}
          className={cn(
            'text-blue-gray hover:bg-transparent hover:text-pink-accent',
          )}
        >
          <Icon name="post-comment" className="w-4" />
        </Button>
        <Button
          variant="link"
          size="sm"
          className="text-blue-gray hover:text-pink-accent p-0 font-light! disabled:opacity-100"
          onClick={repliesCount && !parentId ? isShowReplies : undefined}
          disabled={!(repliesCount && !parentId)}
        >
          {repliesCount && !parentId ? repliesCount + ' replies' : repliesCount}
        </Button>
      </div>
      <Button
        variant="ghost"
        size="sm"
        disabled={isAnonymous}
        onClick={handleLike}
        className={cn(
          'text-blue-gray hover:bg-transparent hover:text-pink-accent',
          {
            'text-pink-accent': liked,
          },
        )}
      >
        {score > 0 ? score : ''}
        <Icon name="post-like" className="w-4" />
      </Button>
      <Button
        variant="ghost"
        size="sm"
        disabled={isAnonymous}
        onClick={handleDislike}
        className={cn(
          'text-blue-gray hover:bg-transparent hover:text-pink-accent flex items-center gap-1',
          { 'text-pink-accent': disliked },
        )}
      >
        <Icon name="post-like" className="w-4 rotate-180" />
      </Button>
      {!isMobile ? (
        <DotsButton postComment={comment} isMyComment={isMyComment} />
      ) : isMyPost ? (
        !isMyComment && (
          <button
            className="size-[22px] relative"
            disabled={!isAbleToSuperLike}
            onClick={handleSuperlike}
          >
            <Image
              src={postAuthorAvatarUrl as string}
              alt="Superlike Author Avatar"
              width={22}
              height={22}
            />
            <div className="bg-white rounded-full size-3.5 p-0.5 absolute bottom-1 right-1 translate-x-1/2 translate-y-1/2">
              <Icon
                name={isSuperliked ? 'logo-heart' : 'empty-heart'}
                className="size-3 "
              />
            </div>
          </button>
        )
      ) : (
        isSuperliked && (
          <div className="size-[22px] relative">
            <Image
              src={postAuthorAvatarUrl as string}
              alt="Superlike Author Avatar"
              width={22}
              height={22}
            />
            <div className="bg-white rounded-full size-3.5 p-0.5 absolute bottom-1 right-1 translate-x-1/2 translate-y-1/2">
              <Icon
                name={isSuperliked ? 'logo-heart' : 'empty-heart'}
                className="size-3 "
              />
            </div>
          </div>
        )
      )}
      <Modal open={isReplyOpened.value} isPost={false}>
        <ReplyForm
          handleCloseModal={isReplyOpened.onFalse}
          postId={comment.postId}
          parentCommentId={comment.parentId || comment.id}
          replyToComment={comment}
        />
      </Modal>
    </div>
  );
};
